/**
 * PinGo brand (direction B "Signal"): the mark is a flagstick whose flag is three signal bars —
 * the pin as a live data feed (also reads as a leaderboard). Geometry is shared with
 * public/icons/icon.svg, scripts/gen-icons.mjs and apps/mobile/scripts/gen-assets.mjs.
 */
import clsx from 'clsx';
import './brand.css';

export interface LogoMarkProps {
  size?: number;
  /** thin lime ring for dark backgrounds (reverse lockup) */
  ring?: boolean;
  className?: string;
  /** accessible name; omit when a visible wordmark follows (decorative) */
  title?: string;
}

export function LogoMark({ size = 32, ring, className, title }: LogoMarkProps) {
  return (
    <svg
      className={clsx('brand-mark', className)}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <rect
        x="1"
        y="1"
        width="62"
        height="62"
        rx="13"
        fill="#0B1020"
        {...(ring ? { stroke: '#C8F31D', strokeOpacity: 0.5, strokeWidth: 1.5 } : {})}
      />
      <rect x="17" y="11" width="5" height="42" rx="2.5" fill="#FFFFFF" />
      <rect x="24.5" y="11" width="24" height="7" rx="2" fill="#C8F31D" />
      <rect x="24.5" y="21" width="17" height="7" rx="2" fill="#C8F31D" fillOpacity={0.72} />
      <rect x="24.5" y="31" width="10" height="7" rx="2" fill="#C8F31D" fillOpacity={0.45} />
    </svg>
  );
}

/** "pingo" + signal dot, set in Inter Tight 900. Inherits colour; size via font-size. */
export function Wordmark({ className, onDark }: { className?: string; onDark?: boolean }) {
  return (
    <span className={clsx('brand-wordmark', onDark && 'brand-wordmark--on-dark', className)}>
      pingo
      <i aria-hidden="true" />
    </span>
  );
}

/** Mark + wordmark lockup. `size` = mark size in px; the wordmark scales with it. */
export function Logo({
  size = 32,
  onDark,
  className,
  wordmarkClassName,
}: {
  size?: number;
  onDark?: boolean;
  className?: string;
  /** e.g. 'visually-hidden'-style class from the shell on phones */
  wordmarkClassName?: string;
}) {
  return (
    <span className={clsx('brand-lockup', className)} style={{ fontSize: size * 0.78 }}>
      <LogoMark size={size} ring={onDark} />
      <Wordmark onDark={onDark} className={wordmarkClassName} />
    </span>
  );
}
